import { supabase } from '../lib/supabase';
import { CalculatedStatsResult } from './calculatedStatsService';

/**
 * Player Scoresheet Service
 * Handles aggregation and calculation of player-level statistics
 */
class PlayerScoresheetService {

  /**
   * Aggregate source stats from scoresheet_logs for a specific player
   * Only aggregates non-calculated stats (is_calculated = false)
   * Only processes logs where player_id IS NOT NULL and is_active = true
   */
  static async aggregateSourceStatsFromLogs(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<void> {
    try {
      console.log(`📊 Aggregating source stats for player ${playerId} in match ${matchId}`);

      // Fetch all logs for this player with player_id and is_active = true
      const { data: logs, error: logsError } = await supabase
        .from('scoresheet_logs')
        .select(`
          stat_type_id,
          action,
          stat_type:stat_types(stat_key, is_calculated)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId)
        .eq('player_id', playerId)
        .eq('is_active', true)
        .not('stat_type_id', 'is', null);

      if (logsError) {
        throw new Error(`Failed to fetch logs: ${logsError.message}`);
      }

      if (!logs || logs.length === 0) {
        console.log(`No logs found for player ${playerId} - skipping aggregation`);
        return;
      }

      // Get all non-calculated stat types
      const { data: statTypes, error: statTypesError } = await supabase
        .from('stat_types')
        .select('id, stat_key')
        .eq('is_calculated', false)
        .eq('is_active', true);

      if (statTypesError) {
        throw new Error(`Failed to fetch stat types: ${statTypesError.message}`);
      }

      // Create a map of stat_type_id -> stat_key for quick lookup
      const statTypeMap = new Map<string, string>();
      statTypes?.forEach(st => {
        statTypeMap.set(st.id, st.stat_key);
      });

      // Aggregate stats: group by stat_type_id and sum increments/decrements
      const aggregatedStats = new Map<string, number>();

      logs.forEach(log => {
        const statType = log.stat_type as any;
        if (!statType || statType.is_calculated === true) {
          return; // Skip calculated stats
        }

        const statTypeId = log.stat_type_id;
        const currentValue = aggregatedStats.get(statTypeId) || 0;

        if (log.action === 'increment') {
          aggregatedStats.set(statTypeId, currentValue + 1);
        } else if (log.action === 'decrement') {
          aggregatedStats.set(statTypeId, Math.max(0, currentValue - 1));
        }
      });

      // Upsert aggregated stats to player_scoresheet table
      const upsertPromises = Array.from(aggregatedStats.entries()).map(async ([statTypeId, value]) => {
        const { error } = await supabase
          .from('player_scoresheet')
          .upsert({
            match_id: matchId,
            team_id: teamId,
            player_id: playerId,
            stat_type_id: statTypeId,
            value: value,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'match_id,team_id,player_id,stat_type_id'
          });

        if (error) {
          throw new Error(`Failed to save stat ${statTypeId}: ${error.message}`);
        }
      });

      await Promise.all(upsertPromises);
      console.log(`✅ Aggregated ${aggregatedStats.size} source stats for player ${playerId}`);
    } catch (error) {
      console.error(`Error aggregating source stats for player ${playerId}:`, error);
      throw error;
    }
  }

  /**
   * Calculate all Attack category stats for a player
   * Reads from player_scoresheet table (source stats) and calculates derived stats
   */
  static async calculatePlayerAttackStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<CalculatedStatsResult> {
    try {
      // Fetch all stats for the player from player_scoresheet
      const { data: stats, error } = await supabase
        .from('player_scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId)
        .eq('player_id', playerId);

      if (error) {
        throw new Error(`Failed to fetch player stats: ${error.message}`);
      }

      // Convert to a map for easy access
      const statsMap = new Map<string, number>();
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          statsMap.set(stat.stat_type.stat_key as string, stat.value || 0);
        }
      });

      // Calculate Attack stats (same formulas as team-level)
      const calculatedStats: CalculatedStatsResult = {};

      // Total Goals
      calculatedStats.total_goals = 
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('own_goal') || 0);

      // Total shots on target
      const shotsOnTarget = 
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('shot_inside_box_on_target') || 0) +
        (statsMap.get('shot_outside_box_on_target') || 0);

      // Total shots off target
      const shotsOffTarget = 
        (statsMap.get('shot_inside_box_off_target') || 0) +
        (statsMap.get('shot_outside_box_off_target') || 0) +
        (statsMap.get('shot_inside_box_crossbar') || 0) +
        (statsMap.get('shot_outside_box_crossbar') || 0) +
        (statsMap.get('shot_inside_box_side_post') || 0) +
        (statsMap.get('shot_outside_box_side_post') || 0) +
        (statsMap.get('shot_inside_box_blocked') || 0) +
        (statsMap.get('shot_outside_box_blocked') || 0);

      // Total shots
      const totalShots = 
        (statsMap.get('shot_inside_box_on_target') || 0) +
        (statsMap.get('shot_inside_box_off_target') || 0) +
        (statsMap.get('shot_inside_box_blocked') || 0) +
        (statsMap.get('shot_inside_box_side_post') || 0) +
        (statsMap.get('shot_inside_box_crossbar') || 0) +
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('shot_outside_box_on_target') || 0) +
        (statsMap.get('shot_outside_box_off_target') || 0) +
        (statsMap.get('shot_outside_box_blocked') || 0) +
        (statsMap.get('shot_outside_box_side_post') || 0) +
        (statsMap.get('shot_outside_box_crossbar') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('penalty_missed') || 0) +
        (statsMap.get('direct_fk') || 0);
      calculatedStats.total_shots = totalShots;

      calculatedStats.shots_on_target = shotsOnTarget;
      calculatedStats.shots_off_target = shotsOffTarget;

      // Goals conversion (%): player's total_goals / player's total_shots * 100
      calculatedStats.goals_conversion_percent = totalShots > 0 
        ? Math.round((calculatedStats.total_goals / totalShots) * 100)
        : 0;

      // Shots accuracy (%): player's shots_on_target / player's total_shots * 100
      calculatedStats.shots_accuracy_percent = totalShots > 0 
        ? Math.round((shotsOnTarget / totalShots) * 100)
        : 0;

      // Penalties attempted (renamed from penalties_awarded for player-level)
      calculatedStats.penalties_attempted = 
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('penalty_missed') || 0);

      // Attacks
      calculatedStats.attacks = 
        totalShots +
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_failure') || 0) +
        (statsMap.get('direct_fk') || 0) +
        (statsMap.get('indirect_fk') || 0) +
        (statsMap.get('corner') || 0) +
        calculatedStats.penalties_attempted;

      // Shots from inside the box
      calculatedStats.shots_from_inside_the_box = 
        (statsMap.get('shot_inside_box_on_target') || 0) +
        (statsMap.get('shot_inside_box_off_target') || 0) +
        (statsMap.get('shot_inside_box_side_post') || 0) +
        (statsMap.get('shot_inside_box_crossbar') || 0) +
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('penalty_missed') || 0);

      // Shots from outside the box
      calculatedStats.shots_from_outside_the_box = 
        (statsMap.get('shot_outside_box_on_target') || 0) +
        (statsMap.get('shot_outside_box_off_target') || 0) +
        (statsMap.get('shot_outside_box_side_post') || 0) +
        (statsMap.get('shot_outside_box_crossbar') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('direct_fk') || 0);

      // Number of crosses
      calculatedStats.number_of_crosses = 
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_failure') || 0);

      // Crossing accuracy (%): player's cross_success / player's number_of_crosses * 100
      const numberOfCrosses = calculatedStats.number_of_crosses;
      const crossSuccess = statsMap.get('cross_success') || 0;
      calculatedStats.crossing_accuracy_percent = numberOfCrosses > 0 
        ? Math.round((crossSuccess / numberOfCrosses) * 100)
        : 0;

      return calculatedStats;
    } catch (error) {
      console.error('Error calculating player Attack stats:', error);
      throw error;
    }
  }

  /**
   * Calculate all Distribution category stats for a player
   */
  static async calculatePlayerDistributionStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<CalculatedStatsResult> {
    try {
      // Fetch all stats for the player
      const { data: stats, error } = await supabase
        .from('player_scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId)
        .eq('player_id', playerId);

      if (error) {
        throw new Error(`Failed to fetch player stats: ${error.message}`);
      }

      // Convert to a map
      const statsMap = new Map<string, number>();
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          statsMap.set(stat.stat_type.stat_key as string, stat.value || 0);
        }
      });

      const calculatedStats: CalculatedStatsResult = {};

      // Passes completed
      const passesCompleted = 
        (statsMap.get('short_pass_success') || 0) +
        (statsMap.get('short_pass_backward_success') || 0) +
        (statsMap.get('long_pass_success') || 0) +
        (statsMap.get('long_pass_backward_success') || 0) +
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_backward_success') || 0);

      // Passes failed
      const passesFailed = 
        (statsMap.get('short_pass_failure') || 0) +
        (statsMap.get('short_pass_backward_failure') || 0) +
        (statsMap.get('long_pass_failure') || 0) +
        (statsMap.get('long_pass_backward_failure') || 0) +
        (statsMap.get('cross_failure') || 0) +
        (statsMap.get('cross_backward_failure') || 0);

      const passesAttempted = passesCompleted + passesFailed;
      calculatedStats.passes_attempted = passesAttempted;
      calculatedStats.passes_completed = passesCompleted;
      calculatedStats.passes_failed = passesFailed;

      // Passing accuracy (%): player's passes_completed / player's passes_attempted * 100
      calculatedStats.passing_accuracy_percent = passesAttempted > 0 
        ? Math.round((passesCompleted / passesAttempted) * 100)
        : 0;

      // Short passes attempted
      const shortPassesAttempted = 
        (statsMap.get('short_pass_success') || 0) +
        (statsMap.get('short_pass_failure') || 0) +
        (statsMap.get('short_pass_backward_success') || 0) +
        (statsMap.get('short_pass_backward_failure') || 0);
      calculatedStats.short_passes_attempted = shortPassesAttempted;

      // Short pass accuracy (%)
      const shortPassesCompleted = 
        (statsMap.get('short_pass_success') || 0) +
        (statsMap.get('short_pass_backward_success') || 0);
      calculatedStats.short_pass_accuracy_percent = shortPassesAttempted > 0 
        ? Math.round((shortPassesCompleted / shortPassesAttempted) * 100)
        : 0;

      // Backward pass (%)
      const backwardPassesCompleted = 
        (statsMap.get('short_pass_backward_success') || 0) +
        (statsMap.get('long_pass_backward_success') || 0) +
        (statsMap.get('cross_backward_success') || 0);
      calculatedStats.backward_pass_percent = shortPassesAttempted > 0 
        ? Math.round((backwardPassesCompleted / shortPassesAttempted) * 100)
        : 0;

      // Backward passes
      calculatedStats.backward_passes = 
        (statsMap.get('short_pass_backward_success') || 0) +
        (statsMap.get('short_pass_backward_failure') || 0);

      // Long passes attempted
      const longPassesAttempted = 
        (statsMap.get('long_pass_success') || 0) +
        (statsMap.get('long_pass_failure') || 0) +
        (statsMap.get('long_pass_backward_success') || 0) +
        (statsMap.get('long_pass_backward_failure') || 0);
      calculatedStats.long_passes_attempted = longPassesAttempted;

      // Long pass accuracy (%)
      const longPassesCompleted = 
        (statsMap.get('long_pass_success') || 0) +
        (statsMap.get('long_pass_backward_success') || 0);
      calculatedStats.long_pass_accuracy_percent = longPassesAttempted > 0 
        ? Math.round((longPassesCompleted / longPassesAttempted) * 100)
        : 0;

      // Crosses attempted
      const crossesAttempted = 
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_failure') || 0) +
        (statsMap.get('cross_backward_success') || 0) +
        (statsMap.get('cross_backward_failure') || 0);
      calculatedStats.crosses_attempted = crossesAttempted;

      // Crossing accuracy (%) - Note: Calculate once only (duplicate from Attack)
      const crossesCompleted = 
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_backward_success') || 0);
      calculatedStats.crossing_accuracy_percent = crossesAttempted > 0 
        ? Math.round((crossesCompleted / crossesAttempted) * 100)
        : 0;

      // Key passes
      calculatedStats.key_passes = 
        (statsMap.get('shot_inside_box_on_target') || 0) +
        (statsMap.get('shot_outside_box_on_target') || 0) +
        (statsMap.get('shot_inside_box_side_post') || 0) +
        (statsMap.get('shot_outside_box_side_post') || 0) +
        (statsMap.get('shot_inside_box_crossbar') || 0) +
        (statsMap.get('shot_outside_box_crossbar') || 0);

      // Chances created
      const totalGoals = 
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('own_goal') || 0);
      calculatedStats.chances_created = calculatedStats.key_passes + totalGoals;

      // Possession percent - Option C: (player_total_actions / match_total_actions) * 100
      // This will be calculated separately as it requires match-level data
      calculatedStats.possession_percent = 0; // Placeholder - calculated separately

      return calculatedStats;
    } catch (error) {
      console.error('Error calculating player Distribution stats:', error);
      throw error;
    }
  }

  /**
   * Calculate all Defense category stats for a player
   */
  static async calculatePlayerDefenseStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<CalculatedStatsResult> {
    try {
      // Fetch all stats for the player
      const { data: stats, error } = await supabase
        .from('player_scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId)
        .eq('player_id', playerId);

      if (error) {
        throw new Error(`Failed to fetch player stats: ${error.message}`);
      }

      // Convert to a map
      const statsMap = new Map<string, number>();
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          statsMap.set(stat.stat_type.stat_key as string, stat.value || 0);
        }
      });

      const calculatedStats: CalculatedStatsResult = {};

      // Balls recovered
      calculatedStats.balls_recovered = 
        (statsMap.get('tackle_success') || 0) +
        (statsMap.get('aerial_won') || 0) +
        (statsMap.get('shot_inside_box_blocked') || 0) +
        (statsMap.get('shot_outside_box_blocked') || 0) +
        (statsMap.get('interception') || 0);

      // Tackles attempted
      const tacklesAttempted = 
        (statsMap.get('tackle_success') || 0) +
        (statsMap.get('tackle_failure') || 0);
      calculatedStats.tackles_attempted = tacklesAttempted;

      // Tackles accuracy (%): player's tackle_success / player's tackles_attempted * 100
      const tacklesWon = statsMap.get('tackle_success') || 0;
      calculatedStats.tackles_accuracy_percent = tacklesAttempted > 0 
        ? Math.round((tacklesWon / tacklesAttempted) * 100)
        : 0;

      // Fouls committed
      calculatedStats.fouls_committed = 
        (statsMap.get('foul_opp_half') || 0) +
        (statsMap.get('foul_own_half') || 0) +
        (statsMap.get('red_card') || 0) +
        (statsMap.get('yellow_card') || 0);

      return calculatedStats;
    } catch (error) {
      console.error('Error calculating player Defense stats:', error);
      throw error;
    }
  }

  /**
   * Calculate all Goalkeeper category stats for a player
   * Calculates for ALL players (will be 0 for non-GKs)
   */
  static async calculatePlayerGoalkeeperStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<CalculatedStatsResult> {
    try {
      // Fetch all stats for the player
      const { data: stats, error } = await supabase
        .from('player_scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId)
        .eq('player_id', playerId);

      if (error) {
        throw new Error(`Failed to fetch player stats: ${error.message}`);
      }

      // Convert to a map
      const statsMap = new Map<string, number>();
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          statsMap.set(stat.stat_type.stat_key as string, stat.value || 0);
        }
      });

      const calculatedStats: CalculatedStatsResult = {};

      // Total claims
      calculatedStats.total_claims = 
        (statsMap.get('open_play_claim') || 0) +
        (statsMap.get('penalty_claim') || 0);

      // Total punches
      calculatedStats.total_punches = 
        (statsMap.get('open_play_punch') || 0) +
        (statsMap.get('penalty_punch') || 0);

      // Total saves
      calculatedStats.total_saves = 
        calculatedStats.total_claims +
        calculatedStats.total_punches;

      return calculatedStats;
    } catch (error) {
      console.error('Error calculating player Goalkeeper stats:', error);
      throw error;
    }
  }

  /**
   * Calculate possession percent for a player
   * Option C: (player_total_actions / match_total_actions) * 100
   */
  static async calculatePlayerPossession(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<number> {
    try {
      // Get player's total actions (excluding percentage stats)
      const { data: playerStats, error: playerError } = await supabase
        .from('player_scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, category)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId)
        .eq('player_id', playerId);

      if (playerError) {
        throw new Error(`Failed to fetch player stats: ${playerError.message}`);
      }

      // Calculate player's total actions
      let playerTotalActions = 0;
      playerStats?.forEach(stat => {
        const statType = stat.stat_type as any;
        if (statType && 
            !statType.stat_key?.includes('percent') && 
            !statType.stat_key?.includes('accuracy')) {
          playerTotalActions += stat.value || 0;
        }
      });

      // Get match total actions from scoresheet table (both teams)
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('home_team_id, away_team_id')
        .eq('id', matchId)
        .single();

      if (matchError || !matchData) {
        throw new Error(`Failed to fetch match data: ${matchError?.message || 'Unknown error'}`);
      }

      const { data: matchStats, error: statsError } = await supabase
        .from('scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, category)
        `)
        .eq('match_id', matchId)
        .in('team_id', [matchData.home_team_id, matchData.away_team_id]);

      if (statsError) {
        throw new Error(`Failed to fetch match stats: ${statsError.message}`);
      }

      // Calculate match total actions
      let matchTotalActions = 0;
      matchStats?.forEach(stat => {
        const statType = stat.stat_type as any;
        if (statType && 
            !statType.stat_key?.includes('percent') && 
            !statType.stat_key?.includes('accuracy')) {
          matchTotalActions += stat.value || 0;
        }
      });

      // Calculate possession percent
      const possessionPercent = matchTotalActions > 0 
        ? Math.round((playerTotalActions / matchTotalActions) * 100)
        : 0;

      return possessionPercent;
    } catch (error) {
      console.error('Error calculating player possession:', error);
      throw error;
    }
  }

  /**
   * Save calculated stats to player_scoresheet table
   */
  static async savePlayerCalculatedStats(
    matchId: string,
    teamId: string,
    playerId: string,
    calculatedStats: CalculatedStatsResult
  ): Promise<void> {
    try {
      // Get calculated stat type IDs
      const { data: statTypes, error: statTypesError } = await supabase
        .from('stat_types')
        .select('id, stat_key')
        .eq('is_calculated', true)
        .in('stat_key', Object.keys(calculatedStats));

      if (statTypesError) {
        throw new Error(`Failed to fetch stat types: ${statTypesError.message}`);
      }

      // Handle penalties_attempted - check if it exists, if not use penalties_awarded
      const penaltiesAttemptedExists = statTypes?.some(st => st.stat_key === 'penalties_attempted');
      if (!penaltiesAttemptedExists && calculatedStats.penalties_attempted !== undefined) {
        // Check if penalties_awarded exists
        const { data: penaltiesAwardedType } = await supabase
          .from('stat_types')
          .select('id, stat_key')
          .eq('stat_key', 'penalties_awarded')
          .eq('is_calculated', true)
          .single();
        
        if (penaltiesAwardedType) {
          // Use penalties_awarded stat type for penalties_attempted value
          statTypes?.push({
            id: penaltiesAwardedType.id,
            stat_key: 'penalties_awarded'
          });
          // Remove penalties_attempted from calculatedStats and add penalties_awarded
          const penaltiesAttemptedValue = calculatedStats.penalties_attempted;
          delete calculatedStats.penalties_attempted;
          calculatedStats.penalties_awarded = penaltiesAttemptedValue;
        }
      }

      // Upsert calculated stats
      const upsertPromises = statTypes?.map(async (statType) => {
        const value = calculatedStats[statType.stat_key];
        if (value !== undefined) {
          const { error } = await supabase
            .from('player_scoresheet')
            .upsert({
              match_id: matchId,
              team_id: teamId,
              player_id: playerId,
              stat_type_id: statType.id,
              value: value,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'match_id,team_id,player_id,stat_type_id'
            });

          if (error) {
            throw new Error(`Failed to save ${statType.stat_key}: ${error.message}`);
          }
        }
      });

      await Promise.all(upsertPromises || []);
    } catch (error) {
      console.error('Error saving player calculated stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all Attack stats for a player
   */
  static async calculateAndSavePlayerAttackStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<void> {
    try {
      const calculatedStats = await this.calculatePlayerAttackStats(matchId, teamId, playerId);
      await this.savePlayerCalculatedStats(matchId, teamId, playerId, calculatedStats);
    } catch (error) {
      console.error('Error calculating and saving player Attack stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all Distribution stats for a player
   */
  static async calculateAndSavePlayerDistributionStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<void> {
    try {
      const calculatedStats = await this.calculatePlayerDistributionStats(matchId, teamId, playerId);
      await this.savePlayerCalculatedStats(matchId, teamId, playerId, calculatedStats);
    } catch (error) {
      console.error('Error calculating and saving player Distribution stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all Defense stats for a player
   */
  static async calculateAndSavePlayerDefenseStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<void> {
    try {
      const calculatedStats = await this.calculatePlayerDefenseStats(matchId, teamId, playerId);
      await this.savePlayerCalculatedStats(matchId, teamId, playerId, calculatedStats);
    } catch (error) {
      console.error('Error calculating and saving player Defense stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all Goalkeeper stats for a player
   */
  static async calculateAndSavePlayerGoalkeeperStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<void> {
    try {
      const calculatedStats = await this.calculatePlayerGoalkeeperStats(matchId, teamId, playerId);
      await this.savePlayerCalculatedStats(matchId, teamId, playerId, calculatedStats);
    } catch (error) {
      console.error('Error calculating and saving player Goalkeeper stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save possession stat for a player
   */
  static async calculateAndSavePlayerPossession(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<void> {
    try {
      const possessionPercent = await this.calculatePlayerPossession(matchId, teamId, playerId);
      
      // Get possession stat type ID
      const { data: statType, error: statTypeError } = await supabase
        .from('stat_types')
        .select('id')
        .eq('stat_key', 'possession_percent')
        .eq('is_calculated', true)
        .single();

      if (statTypeError) {
        throw new Error(`Failed to fetch possession stat type: ${statTypeError.message}`);
      }

      // Upsert possession stat
      const { error } = await supabase
        .from('player_scoresheet')
        .upsert({
          match_id: matchId,
          team_id: teamId,
          player_id: playerId,
          stat_type_id: statType.id,
          value: possessionPercent,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'match_id,team_id,player_id,stat_type_id'
        });

      if (error) {
        throw new Error(`Failed to save possession_percent: ${error.message}`);
      }
    } catch (error) {
      console.error('Error calculating and saving player possession:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all stats for a single player
   * This is the main method that does both aggregation and calculation
   */
  static async calculateAndSaveAllPlayerStats(
    matchId: string,
    teamId: string,
    playerId: string
  ): Promise<void> {
    try {
      console.log(`🚀 Starting player stats calculation for player ${playerId} in match ${matchId}`);

      // Step 1: Aggregate source stats from logs
      await this.aggregateSourceStatsFromLogs(matchId, teamId, playerId);

      // Step 2: Calculate and save all calculated stats
      await Promise.all([
        this.calculateAndSavePlayerAttackStats(matchId, teamId, playerId),
        this.calculateAndSavePlayerDistributionStats(matchId, teamId, playerId),
        this.calculateAndSavePlayerDefenseStats(matchId, teamId, playerId),
        this.calculateAndSavePlayerGoalkeeperStats(matchId, teamId, playerId),
        this.calculateAndSavePlayerPossession(matchId, teamId, playerId)
      ]);

      console.log(`✅ Completed player stats calculation for player ${playerId}`);
    } catch (error) {
      console.error(`❌ Error calculating all player stats for player ${playerId}:`, error);
      throw error;
    }
  }

  /**
   * Get all players who have at least one log entry with player_id for a match
   */
  static async getPlayersWithStats(matchId: string): Promise<Array<{ player_id: string; team_id: string }>> {
    try {
      const { data, error } = await supabase
        .from('scoresheet_logs')
        .select('player_id, team_id')
        .eq('match_id', matchId)
        .eq('is_active', true)
        .not('player_id', 'is', null)
        .order('player_id');

      if (error) {
        throw new Error(`Failed to fetch players with stats: ${error.message}`);
      }

      // Get unique player-team combinations
      const uniquePlayers = new Map<string, { player_id: string; team_id: string }>();
      data?.forEach(log => {
        if (log.player_id && log.team_id) {
          const key = `${log.player_id}-${log.team_id}`;
          if (!uniquePlayers.has(key)) {
            uniquePlayers.set(key, {
              player_id: log.player_id,
              team_id: log.team_id
            });
          }
        }
      });

      return Array.from(uniquePlayers.values());
    } catch (error) {
      console.error('Error getting players with stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all player stats for all players in a match
   * Only processes players who have at least one log entry with player_id
   */
  static async calculateAndSaveAllPlayersStats(matchId: string): Promise<void> {
    try {
      console.log(`🚀 Starting player stats calculation for all players in match ${matchId}`);

      // Get all players who have stats
      const players = await this.getPlayersWithStats(matchId);

      if (players.length === 0) {
        console.log(`No players with stats found for match ${matchId}`);
        return;
      }

      console.log(`📊 Found ${players.length} players with stats`);

      // Calculate stats for each player
      const promises = players.map(({ player_id, team_id }) =>
        this.calculateAndSaveAllPlayerStats(matchId, team_id, player_id)
      );

      await Promise.all(promises);

      console.log(`✅ Completed player stats calculation for all ${players.length} players`);
    } catch (error) {
      console.error(`❌ Error calculating all players stats:`, error);
      throw error;
    }
  }
}

export const playerScoresheetService = PlayerScoresheetService;


